import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import WorkflowStage from './components/WorkflowStage';
import WorkflowFilters from './components/WorkflowFilters';
import JobDetailsModal from './components/JobDetailsModal';
import NewJobModal from './components/NewJobModal';
import AssignOperatorModal from './components/AssignOperatorModal';
import MobileWorkflowTabs from './components/MobileWorkflowTabs';

const ProductionWorkflow = () => {
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    design: 'all',
    operator: 'all',
    priority: 'all',
    status: 'all',
    jobId: '',
    minFabric: '',
    maxFabric: '',
    minPieces: '',
    maxPieces: ''
  });

  const [selectedJob, setSelectedJob] = useState(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isNewJobModalOpen, setIsNewJobModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [draggedJob, setDraggedJob] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [mobileActiveStage, setMobileActiveStage] = useState('digital-printing');
  const [notifications, setNotifications] = useState([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  const stages = [
    {
      id: 'digital-printing',
      name: 'Digital Printing',
      completionRate: 85,
      operators: [
        { id: 1, name: 'Rajesh Kumar', activeJobs: 3, status: 'available' },
        { id: 2, name: 'Sunita Devi', activeJobs: 1, status: 'available' }
      ]
    },
    {
      id: 'cutting',
      name: 'Cutting',
      completionRate: 72,
      operators: [
        { id: 3, name: 'Priya Sharma', activeJobs: 2, status: 'available' },
        { id: 4, name: 'Vikram Singh', activeJobs: 3, status: 'busy' }
      ]
    },
    {
      id: 'stitching',
      name: 'Stitching',
      completionRate: 60,
      operators: [
        { id: 5, name: 'Amit Patel', activeJobs: 4, status: 'busy' },
        { id: 6, name: 'Meera Gupta', activeJobs: 2, status: 'available' }
      ]
    }
  ];

  const [jobs, setJobs] = useState([
    {
      id: 1,
      jobId: 'JOB001',
      designName: 'Floral Summer Collection',
      fabricMeters: 45.5,
      totalPieces: 120,
      priority: 'high',
      dueDate: '2025-01-28',
      assignedOperator: 'Rajesh Kumar',
      stage: 'digital-printing',
      progress: 75,
      status: 'in-progress',
      sizeDistribution: { S: 20, M: 30, L: 35, XL: 25, '2XL': 10 },
      defects: 0,
      instructions: 'Use premium quality ink for vibrant colors',
      createdAt: '2025-01-26T08:30:00'
    },
    {
      id: 2,
      jobId: 'JOB002',
      designName: 'Geometric Modern',
      fabricMeters: 32.0,
      totalPieces: 80,
      priority: 'medium',
      dueDate: '2025-01-30',
      assignedOperator: 'Sunita Devi',
      stage: 'digital-printing',
      progress: 45,
      status: 'in-progress',
      sizeDistribution: { S: 15, M: 20, L: 25, XL: 15, '2XL': 5 },
      defects: 0,
      instructions: 'Ensure precise alignment for geometric patterns',
      createdAt: '2025-01-26T09:15:00'
    },
    {
      id: 3,
      jobId: 'JOB003',
      designName: 'Vintage Classic',
      fabricMeters: 28.5,
      totalPieces: 65,
      priority: 'low',
      dueDate: '2025-02-02',
      assignedOperator: 'Priya Sharma',
      stage: 'cutting',
      progress: 90,
      status: 'in-progress',
      sizeDistribution: { S: 10, M: 15, L: 20, XL: 15, '2XL': 5 },
      defects: 2,
      instructions: 'Handle vintage fabric with extra care',
      createdAt: '2025-01-25T14:20:00'
    },
    {
      id: 4,
      jobId: 'JOB004',
      designName: 'Abstract Art Series',
      fabricMeters: 52.0,
      totalPieces: 140,
      priority: 'high',
      dueDate: '2025-01-29',
      assignedOperator: 'Vikram Singh',
      stage: 'cutting',
      progress: 60,
      status: 'in-progress',
      sizeDistribution: { S: 25, M: 35, L: 40, XL: 30, '2XL': 10 },
      defects: 1,
      instructions: 'Complex cutting pattern - follow template precisely',
      createdAt: '2025-01-25T16:45:00'
    },
    {
      id: 5,
      jobId: 'JOB005',
      designName: 'Nature Inspired',
      fabricMeters: 38.0,
      totalPieces: 95,
      priority: 'medium',
      dueDate: '2025-02-01',
      assignedOperator: 'Amit Patel',
      stage: 'stitching',
      progress: 30,
      status: 'in-progress',
      sizeDistribution: { S: 18, M: 25, L: 28, XL: 20, '2XL': 4 },
      defects: 0,
      instructions: 'Use reinforced stitching for durability',
      createdAt: '2025-01-24T11:30:00'
    },
    {
      id: 6,
      jobId: 'JOB006',
      designName: 'Minimalist Design',
      fabricMeters: 25.0,
      totalPieces: 50,
      priority: 'low',
      dueDate: '2025-02-03',
      assignedOperator: 'Meera Gupta',
      stage: 'stitching',
      progress: 100,
      status: 'completed',
      sizeDistribution: { S: 8, M: 12, L: 15, XL: 12, '2XL': 3 },
      defects: 0,
      instructions: 'Clean finishing required for minimalist aesthetic',
      createdAt: '2025-01-23T13:15:00'
    }
  ]);

  // Filter jobs based on current filters
  const getFilteredJobs = () => {
    return jobs.filter(job => {
      if (filters.fromDate && new Date(job.createdAt) < new Date(filters.fromDate)) return false;
      if (filters.toDate && new Date(job.createdAt) > new Date(filters.toDate)) return false;
      if (filters.design !== 'all' && !job.designName.toLowerCase().includes(filters.design.toLowerCase())) return false;
      if (filters.operator !== 'all' && job.assignedOperator !== filters.operator) return false;
      if (filters.priority !== 'all' && job.priority !== filters.priority) return false;
      if (filters.status !== 'all' && job.status !== filters.status) return false;
      if (filters.jobId && !job.jobId.toLowerCase().includes(filters.jobId.toLowerCase())) return false;
      if (filters.minFabric && job.fabricMeters < parseFloat(filters.minFabric)) return false;
      if (filters.maxFabric && job.fabricMeters > parseFloat(filters.maxFabric)) return false;
      if (filters.minPieces && job.totalPieces < parseInt(filters.minPieces)) return false;
      if (filters.maxPieces && job.totalPieces > parseInt(filters.maxPieces)) return false;
      return true;
    });
  };

  const getJobsByStage = (stageId) => {
    return getFilteredJobs().filter(job => job.stage === stageId);
  };

  // Check for bottlenecks and delays
  useEffect(() => {
    const checkNotifications = () => {
      const newNotifications = [];
      const today = new Date();

      jobs.forEach(job => {
        const dueDate = new Date(job.dueDate);
        const daysDiff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

        if (daysDiff <= 1 && job.progress < 80) {
          newNotifications.push({
            id: `delay-${job.id}`,
            type: 'warning',
            title: 'Potential Delay',
            message: `Job ${job.jobId} may miss deadline`,
            jobId: job.jobId
          });
        }

        if (job.defects > 0) {
          newNotifications.push({
            id: `defect-${job.id}`,
            type: 'error',
            title: 'Quality Issue',
            message: `${job.defects} defective pieces in ${job.jobId}`,
            jobId: job.jobId
          });
        }
      });

      setNotifications(newNotifications);
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [jobs]);

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setIsJobModalOpen(true);
  };

  const handleJobSave = (updatedJob) => {
    setJobs(prev => prev.map(job =>
      job.id === updatedJob.id ? { ...job, ...updatedJob } : job
    ));
  };

  const handleNewJob = (newJob) => {
    setJobs(prev => [...prev, newJob]);
  };

  const handleAssignOperator = (assignment) => {
    if (assignment.jobs && assignment.jobs.length > 0) {
      // Assign to selected jobs
      setJobs(prev => prev.map(job =>
        assignment.jobs.includes(job.id)
          ? { ...job, assignedOperator: assignment.operatorName, stage: assignment.stage }
          : job
      ));
    }
    setSelectedJobs([]);
  };

  const handleRecordCompletion = (jobId) => {
    setJobs(prev => prev.map(job =>
      job.id === jobId
        ? { ...job, progress: 100, status: 'completed' }
        : job
    ));
  };

  const handleDragStart = (e, job) => {
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (draggedJob && draggedJob.stage !== stageId) {
      setJobs(prev => prev.map(job =>
        job.id === draggedJob.id
          ? { ...job, stage: stageId, progress: 0, status: 'pending' }
          : job
      ));
    }
    setDraggedJob(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar onCollapseChange={setIsSidebarCollapsed} />
      <main
        className={`
        transition-all duration-300
        pt-16
        ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        min-h-[calc(100vh-4rem)]
        px-4 sm:px-6
      `}
      >
        <div className="flex-1 flex flex-col">
          <Breadcrumb />

          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Production Workflow</h1>
              <p className="text-muted-foreground mt-1 text-sm md:text-base">
                Manage digital printing, cutting, and stitching operations
              </p>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-2">
              <div className="bg-card border border-border rounded-lg px-3 py-2 min-w-[120px]">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} color="var(--color-primary)" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{jobs.filter(j => j.status === 'in-progress').length}</p>
                    <p className="text-xs text-muted-foreground">Active Jobs</p>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg px-3 py-2 min-w-[120px]">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{notifications.length}</p>
                    <p className="text-xs text-muted-foreground">Alerts</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          {notifications.length > 0 && (
            <div className="mb-6">
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Bell" size={16} color="var(--color-warning)" />
                  <h3 className="font-medium text-warning">Production Alerts</h3>
                </div>
                <div className="space-y-2">
                  {notifications.slice(0, 3).map((notification) => (
                    <div key={notification.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{notification.message}</span>
                      <Button variant="ghost" size="xs" iconName="X" iconSize={12} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <WorkflowFilters
            filters={filters}
            onFiltersChange={setFilters}
            onNewJob={() => setIsNewJobModalOpen(true)}
            onAssignOperator={() => setIsAssignModalOpen(true)}
          />

          {/* Desktop Workflow View */}
          <div className="hidden md:block overflow-x-auto pb-4">
            <div className="flex space-x-4 min-h-[600px] w-max min-w-full mb-8">
              {stages.map((stage) => (
                <WorkflowStage
                  key={stage.id}
                  stage={stage}
                  jobs={getJobsByStage(stage.id)}
                  onJobClick={handleJobClick}
                  onAssignOperator={() => setIsAssignModalOpen(true)}
                  onRecordCompletion={handleRecordCompletion}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              ))}
            </div>
          </div>

          {/* Mobile Workflow View */}
          <MobileWorkflowTabs
            activeStage={mobileActiveStage}
            onStageChange={setMobileActiveStage}
            stages={stages}
            jobs={getFilteredJobs()}
            onJobClick={handleJobClick}
            onRecordCompletion={handleRecordCompletion}
          />
        </div>
      </main>

      {/* Modals */}
      <JobDetailsModal
        job={selectedJob}
        isOpen={isJobModalOpen}
        onClose={() => {
          setIsJobModalOpen(false);
          setSelectedJob(null);
        }}
        onSave={handleJobSave}
      />

      <NewJobModal
        isOpen={isNewJobModalOpen}
        onClose={() => setIsNewJobModalOpen(false)}
        onSave={handleNewJob}
      />

      <AssignOperatorModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onAssign={handleAssignOperator}
        selectedJobs={selectedJobs}
      />
    </div>
  );
};

export default ProductionWorkflow;